import React from "react";
import {
  Box,
  Typography,
  FormControl,
  MenuItem,
  Radio,
  FormControlLabel,
  Grid,
  Divider
} from "@mui/material";
import { TextField, Select, RadioGroup } from "formik-mui";
import { Field } from "formik";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { PropTypes } from "prop-types";

const useStyles = makeStyles(theme => ({
  label: {
    fontWeight: "bold",
    minWidth: 150
  },
  divider: {
    width: `calc(100% - ${theme.spacing(3)})`,
    margin: theme.spacing(3, 0, 0.25, 1.5)
  }
}));

const timePeriodOptions = ["days", "months", "years"];

const PolicySettingsDefault = ({ values, courseType, graceHillContent }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Box
      display="flex"
      flexDirection="column"
      mt={4}
      data-qa="course-settings-default"
    >
      <Box display="flex" mb={3}>
        <Typography variant="h5">
          {t("manageCourses.courseSettingsDefaultSettingsHeader")}
        </Typography>
      </Box>
      <Grid container spacing={3} alignItems="center">
        <Grid container item spacing={3}>
          <Grid item xs={12} sm={6}>
            <Field
              variant="filled"
              fullWidth
              label={t("manageCourses.courseSettingsTimeToCompleteField", {
                timePeriod: t(values.timeToCompleteUnit)
              })}
              component={TextField}
              name="timeToComplete"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth hiddenLabel>
              <Field
                type="text"
                name="timeToCompleteUnit"
                component={Select}
                inputProps={{
                  name: "timeToCompleteUnit",
                  id: "timeToCompleteUnit"
                }}
                variant="filled"
                fullWidth
                hiddenLabel
              >
                {timePeriodOptions.map(option => (
                  <MenuItem value={option} key={option}>
                    {t(option.toLowerCase())}
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" className={classes.label}>
            {t("manageCourses.courseSettingsRecordsScoreField")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Field
              type="text"
              variant="filled"
              component={RadioGroup}
              name="recordScore"
              row
            >
              <FormControlLabel
                disabled={graceHillContent}
                value="yes"
                control={
                  <Radio
                    color="primary"
                    data-qa="course-settings-records-score-yes"
                  />
                }
                label={t("yes")}
              />
              <FormControlLabel
                disabled={graceHillContent}
                value="no"
                control={
                  <Radio
                    color="primary"
                    data-qa="course-settings-records-score-no"
                  />
                }
                label={t("no")}
              />
            </Field>
          </FormControl>
        </Grid>
        {values.recordScore === "yes" && (
          <Grid item xs={12}>
            <Box width="50%">
              <Field
                variant="filled"
                fullWidth
                label={t("manageCourses.courseSettingsPassingScoreField")}
                component={TextField}
                name="passingScore"
                inputProps={{
                  "data-qa": "course-settings-passing-score-input"
                }}
              />
            </Box>
          </Grid>
        )}
        <Divider className={classes.divider} />
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" className={classes.label}>
            {t("manageCourses.courseSettingsRecurringEventField")}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth data-qa="course-settings-recurring-event">
            <Field
              type="text"
              variant="filled"
              component={RadioGroup}
              name="recurringEvent"
              row
            >
              <FormControlLabel
                value="yes"
                control={<Radio color="primary" />}
                label={t("yes")}
              />
              <FormControlLabel
                value="no"
                control={<Radio color="primary" />}
                label={t("no")}
              />
            </Field>
          </FormControl>
        </Grid>
        {values.recurringEvent === "yes" && (
          <Grid container item spacing={3}>
            <Grid item xs={12} sm={6}>
              <Field
                variant="filled"
                fullWidth
                label={t("manageCourses.courseSettingsRecurrenceIntervalField")}
                component={TextField}
                name="recurringInterval"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth hiddenLabel>
                <Field
                  type="text"
                  name="recurringIntervalUnit"
                  component={Select}
                  inputProps={{
                    name: "recurringIntervalUnit",
                    id: "recurringIntervalUnit"
                  }}
                  variant="filled"
                  fullWidth
                  hiddenLabel
                >
                  <MenuItem value="days">{t("days")}</MenuItem>
                  <MenuItem value="weeks">{t("weeks")}</MenuItem>
                  <MenuItem value="years">{t("years")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>
          </Grid>
        )}
        {values.showEquivalentToggle && (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" className={classes.label}>
                {t("manageCourses.courseSettingsAllowEquivalentsField")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Field
                  type="text"
                  variant="filled"
                  component={RadioGroup}
                  name="allowEquivalents"
                  row
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio color="primary" />}
                    label={t("yes")}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio color="primary" />}
                    label={t("no")}
                  />
                </Field>
              </FormControl>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default PolicySettingsDefault;

PolicySettingsDefault.propTypes = {
  values: PropTypes.shape({
    courseTitle: PropTypes.string,
    description: PropTypes.string,
    tags: PropTypes.array,
    durationHours: PropTypes.number,
    durationMinutes: PropTypes.number,
    includeCertificate: PropTypes.string,
    displayInLibrary: PropTypes.string,
    isActive: PropTypes.string,
    timeToComplete: PropTypes.any,
    timeToCompleteUnit: PropTypes.string,
    recordScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    passingScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recurringEvent: PropTypes.string,
    recurringInterval: PropTypes.number,
    recurringIntervalUnit: PropTypes.string,
    allowEquivalents: PropTypes.string,
    courseImage: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  })
};
